import { useEffect, useLayoutEffect, useState } from "react";
import type { SiteShell } from "../server/shell";

/** Selected private data stays outside router/prefetch caches and is discarded on leave/hide. */
export function usePublishedData<T>(initial: T, shell: SiteShell, path: string, operation: string) {
  const enabled = !!shell.publication?.enhancedPaths?.includes(path);
  const [value, setValue] = useState({
    initial,
    path,
    data: initial,
    pending: enabled,
    failed: false,
  });
  useEffect(() => {
    setValue({ initial, path, data: initial, pending: enabled, failed: false });
    if (!enabled) return;
    let stopped = false;
    let controller: AbortController | undefined;
    const refresh = async () => {
      if (stopped || document.visibilityState === "hidden") return;
      controller?.abort();
      const current = (controller = new AbortController());
      try {
        const response = await fetch(
          `/api/published-site/${operation}?creator=${encodeURIComponent(shell.creator.id)}&path=${encodeURIComponent(path)}&native=${["q", "collection", "category", "sort"].some((key) => new URLSearchParams(location.search).has(key)) ? "1" : "0"}${path === "/join" ? "&" + new URLSearchParams([...new URLSearchParams(location.search)].filter(([key]) => ["course", "content"].includes(key))) : ""}`,
          {
            credentials: "same-origin",
            cache: "no-store",
            signal: current.signal,
          },
        );
        if (!response.ok) throw new Error("Private site data unavailable");
        const result = (await response.json()) as {
          creatorId?: string;
          viewer?: unknown;
          audience?: unknown;
          data?: T;
        };
        if (stopped || current.signal.aborted || result.creatorId !== shell.creator.id) return;
        window.dispatchEvent(
          new CustomEvent("drate:published-viewer", {
            detail: {
              creatorId: result.creatorId,
              viewer: result.viewer,
              audience: result.audience,
            },
          }),
        );
        const redirect = result.data as { kind?: unknown; to?: unknown } | undefined;
        if (redirect?.kind === "missing") throw new Error("Private page unavailable");
        if (redirect?.kind === "redirect" && typeof redirect.to === "string") {
          const destination = new URL(redirect.to, location.origin);
          if (destination.origin === location.origin && destination.href !== location.href) {
            location.replace(destination.href);
            return;
          }
          throw new Error("Invalid private page redirect");
        }
        setValue({ initial, path, data: result.data ?? initial, pending: false, failed: false });
      } catch {
        if (!stopped && !current.signal.aborted)
          setValue({ initial, path, data: initial, pending: false, failed: true });
      }
    };
    const visible = () => {
      controller?.abort();
      if (document.visibilityState === "hidden") {
        if (operation !== "account")
          setValue({ initial, path, data: initial, pending: true, failed: false });
      } else void refresh();
    };
    void refresh();
    const interval = window.setInterval(() => void refresh(), 30_000);
    window.addEventListener("focus", visible);
    document.addEventListener("visibilitychange", visible);
    return () => {
      stopped = true;
      controller?.abort();
      window.clearInterval(interval);
      window.removeEventListener("focus", visible);
      document.removeEventListener("visibilitychange", visible);
    };
  }, [initial, enabled, path, operation, shell.creator.id]);
  useLayoutEffect(() => {
    if (
      enabled &&
      value.initial === initial &&
      value.path === path &&
      !value.pending &&
      !value.failed
    )
      performance.mark("drate:private-page-ready", { detail: { path } });
  }, [enabled, initial, path, value]);
  return enabled && value.initial === initial && value.path === path
    ? value
    : { data: initial, pending: enabled, failed: false };
}
